// apps/web/src/features/cargo/components/CargoEditModal.tsx
//
// Row edit modal (#1232) for admin / logist_ru / logist_cn. RHF + zodResolver
// over the edit form schema; numeric-like fields validate + submit normalized
// source strings via `toUpdatePatch`. Field-level ability:
//   - chinaPaymentAmount: read-only unless `canManageChinaPayment` (logist_ru
//     sees it read-only);
//   - paymentToCarrierStatus: included only when `canManageCarrierPayment`.
// The status selector offers 3 options: in_transit / arrived_rf / issued
// (#2161). "issued" is a form-only derived state coupled to `timeCargoRelease`
// by a strict biconditional: selecting "issued" requires a release date, and
// entering a release date sets the status to "issued". A non-issued status
// always clears the release date. `toUpdatePatch` maps issued -> arrived_rf +
// non-null release date before sending to the wire (the ADD modal keeps the
// original 2-state selector — wire has no 3rd status).

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { CargoArticleWire } from '@magimp/shared';

import { Button, EditorModal, useEditorGuard } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { canManageCarrierPayment, canManageChinaPayment, type CargoActor } from '../ability.js';
import { useUpdateCargo } from '../mutations.js';
import {
  CargoEditFormSchema,
  cargoRowToEditValues,
  toUpdatePatch,
  type CargoEditFormValues,
} from '../forms/schemas.js';
import {
  cargoCarrierPaymentOptions,
  cargoEditStatusOptions,
  useCargoDictionaryOptions,
  type CargoManagerOption,
} from '../forms/useCargoFormOptions.js';
import { CargoDateField, CargoSelectField, CargoTextField } from '../forms/fields.js';

export interface CargoEditModalProps {
  row: CargoArticleWire;
  managerOptions: readonly CargoManagerOption[];
  onClose: () => void;
}

export function CargoEditModal({ row, managerOptions, onClose }: CargoEditModalProps) {
  const { t } = useTranslation(['cargo', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const actor: CargoActor = { user, permissions };
  const update = useUpdateCargo(row.id);
  const showToast = useToastStore((s) => s.show);

  const chinaEditable = canManageChinaPayment(actor);
  const carrierPaymentEditable = canManageCarrierPayment(actor);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<CargoEditFormValues>({
    resolver: zodResolver(CargoEditFormSchema),
    defaultValues: cargoRowToEditValues(row),
  });

  const dict = useCargoDictionaryOptions(true, row.carrier ?? '', row.pathFormat ?? '');

  // WHY (#2161): biconditional coupling between status "issued" and the release
  // date. Guards prevent setValue feedback loops on mount (only the changed
  // field triggers its rule).
  useEffect(() => {
    const sub = watch((values, { name }) => {
      const hasDate = (values.timeCargoRelease ?? '').trim().length > 0;
      if (name === 'timeCargoRelease') {
        // Rule 2: entering a date ⇒ status issued
        if (hasDate && values.status !== 'issued') {
          setValue('status', 'issued', { shouldDirty: true, shouldValidate: true });
          // Rule 4: clearing the date while issued ⇒ revert to arrived_rf
        } else if (!hasDate && values.status === 'issued') {
          setValue('status', 'arrived_rf', { shouldDirty: true, shouldValidate: true });
        }
      } else if (name === 'status') {
        // Rule 3: choosing a non-issued status ⇒ clear the date
        if ((values.status === 'in_transit' || values.status === 'arrived_rf') && hasDate) {
          setValue('timeCargoRelease', '', { shouldDirty: true, shouldValidate: true });
        }
      }
    });
    return () => sub.unsubscribe();
  }, [watch, setValue]);

  const numericError = (key: keyof CargoEditFormValues): string | undefined =>
    errors[key] ? t('cargo:forms.errors.numeric') : undefined;

  const onSubmit = (values: CargoEditFormValues) => {
    const patch = toUpdatePatch(values, {
      includeChinaPayment: chinaEditable,
      includeCarrierPayment: carrierPaymentEditable,
      originalCarrier: row.carrier ?? null,
      originalPathFormat: row.pathFormat ?? null,
    });
    update.mutate(patch, {
      onSuccess: (saved) => {
        reset(cargoRowToEditValues(saved));
        showToast({ tone: 'success', text: t('cargo:forms.toasts.updated') });
        onClose();
      },
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };

  const managerSelectOptions = managerOptions.map((m) => ({ value: m.id, label: m.name }));
  // FR-5/BR-4: a stored manager who is no longer assignable (deactivated / role
  // changed) is absent from the roster; re-append her from the row's own ref so
  // saving never silently clears or rewrites the responsible-manager field.
  const current = row.responsibleManager;
  if (current && !managerSelectOptions.some((o) => o.value === current.id)) {
    managerSelectOptions.push({ value: current.id, label: current.name });
  }
  const unset = { value: '', label: t('cargo:forms.unset') };

  return (
    <EditorModal
      open
      title={t('cargo:forms.editTitle', { article: row.article })}
      width={560}
      dirty={isDirty}
      busy={update.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="cargo-edit-form">
        <CargoTextField
          control={control}
          name="article"
          label={t('cargo:forms.fields.article')}
          error={errors.article ? t('cargo:forms.errors.required') : undefined}
        />
        <CargoSelectField
          control={control}
          name="responsibleManagerId"
          label={t('cargo:forms.fields.manager')}
          options={managerSelectOptions}
          placeholderOption={unset}
          testId="cargo-edit-manager"
        />
        <CargoSelectField
          control={control}
          name="carrier"
          label={t('cargo:forms.fields.carrier')}
          options={dict.carrierOptions}
          placeholderOption={unset}
          testId="cargo-edit-carrier"
        />
        <CargoSelectField
          control={control}
          name="pathFormat"
          label={t('cargo:forms.fields.pathFormat')}
          options={dict.pathFormatOptions}
          placeholderOption={unset}
          testId="cargo-edit-path-format"
        />
        <CargoSelectField
          control={control}
          name="status"
          label={t('cargo:forms.fields.status')}
          options={cargoEditStatusOptions(t('cargo:status.issued'))}
          testId="cargo-edit-status"
        />
        <CargoTextField
          control={control}
          name="nameGoods"
          label={t('cargo:forms.fields.nameGoods')}
        />
        <CargoTextField
          control={control}
          name="numberOfSeats"
          label={t('cargo:forms.fields.numberOfSeats')}
          error={numericError('numberOfSeats')}
        />
        <CargoTextField
          control={control}
          name="weight"
          label={t('cargo:forms.fields.weight')}
          error={numericError('weight')}
        />
        <CargoTextField
          control={control}
          name="volume"
          label={t('cargo:forms.fields.volume')}
          error={numericError('volume')}
        />
        <CargoTextField
          control={control}
          name="transportationTariff"
          label={t('cargo:forms.fields.transportationTariff')}
        />
        <CargoTextField
          control={control}
          name="transportationTariffForClients"
          label={t('cargo:forms.fields.transportationTariffForClients')}
        />
        <CargoTextField
          control={control}
          name="costGoods"
          label={t('cargo:forms.fields.costGoods')}
          error={numericError('costGoods')}
        />
        <CargoTextField
          control={control}
          name="insuranceCost"
          label={t('cargo:forms.fields.insuranceCost')}
          error={numericError('insuranceCost')}
        />
        <CargoTextField
          control={control}
          name="packagingCost"
          label={t('cargo:forms.fields.packagingCost')}
          error={numericError('packagingCost')}
        />
        <CargoTextField
          control={control}
          name="totalCost"
          label={t('cargo:forms.fields.totalCost')}
          error={numericError('totalCost')}
        />
        <CargoTextField
          control={control}
          name="addressTransportationCost"
          label={t('cargo:forms.fields.addressTransportationCost')}
          error={numericError('addressTransportationCost')}
        />
        <CargoDateField
          control={control}
          name="timeFromChina"
          label={t('cargo:forms.fields.timeFromChina')}
        />
        <CargoTextField
          control={control}
          name="chinaPaymentAmount"
          label={t('cargo:forms.fields.chinaPaymentAmount')}
          error={chinaEditable ? numericError('chinaPaymentAmount') : undefined}
          readOnly={!chinaEditable}
        />
        <CargoTextField
          control={control}
          name="chinaWarehouseDispatchAmount"
          label={t('cargo:forms.fields.chinaWarehouseDispatchAmount')}
          error={chinaEditable ? numericError('chinaWarehouseDispatchAmount') : undefined}
          readOnly={!chinaEditable}
        />
        <CargoTextField
          control={control}
          name="chinaWarehouseInspectionAmount"
          label={t('cargo:forms.fields.chinaWarehouseInspectionAmount')}
          error={chinaEditable ? numericError('chinaWarehouseInspectionAmount') : undefined}
          readOnly={!chinaEditable}
        />
        <CargoTextField
          control={control}
          name="chinaWarehousePackagingAmount"
          label={t('cargo:forms.fields.chinaWarehousePackagingAmount')}
          error={chinaEditable ? numericError('chinaWarehousePackagingAmount') : undefined}
          readOnly={!chinaEditable}
        />
        <CargoTextField
          control={control}
          name="prr"
          label={t('cargo:forms.fields.prr')}
          error={numericError('prr')}
        />
        <CargoTextField
          control={control}
          name="tatCost"
          label={t('cargo:forms.fields.tatCost')}
          error={numericError('tatCost')}
        />
        {carrierPaymentEditable && (
          <CargoSelectField
            control={control}
            name="paymentToCarrierStatus"
            label={t('cargo:forms.fields.paymentToCarrierStatus')}
            options={cargoCarrierPaymentOptions()}
            testId="cargo-edit-carrier-payment"
          />
        )}
        <CargoDateField
          control={control}
          name="timeCargoArrivalToRf"
          label={t('cargo:forms.fields.timeCargoArrivalToRf')}
        />
        <CargoDateField
          control={control}
          name="timeCargoRelease"
          label={t('cargo:forms.fields.timeCargoRelease')}
          testId="cargo-edit-release-date"
          error={errors.timeCargoRelease ? t('cargo:forms.errors.releaseDateRequired') : undefined}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={update.isPending}>
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
